import { generateKeyBetween } from 'fractional-indexing';

/**
 * ============================================================================
 * COMPOSITE SORT ORDER SYSTEM
 * ============================================================================
 *
 * This module provides the composite sort_order abstraction that combines
 * a wall-clock date with a fractional index into a single sortable string.
 *
 * Format: "YYYY-MM-DDTHH:mm:ss.sss--{fractional_index}"
 * Example: "2024-01-15T12:00:00.000--a5"
 *
 * ## Date Philosophy: Wall-Clock Time (No UTC)
 *
 * All dates represent the camera/wall-clock time — never UTC.
 * A video taken April 30 at 8:30am AEST is stored as "2024-04-30T08:30:00.000",
 * NOT converted to UTC ("2024-04-29T22:30:00.000Z"). This ensures the displayed
 * calendar date always matches what the user expects.
 *
 * - No "Z" suffix, no timezone offset — just bare local datetime
 * - NEVER call toISOString() for dates going into sort_order (it converts to UTC)
 * - ALWAYS use normalizeToWallClock() from this module
 *
 * ## Why Composite?
 *
 * The previous system used two independent fields:
 * - `position` (fractional index) — playback order
 * - `date_taken` (timestamp) — visual grouping
 *
 * These could drift apart (e.g., after drag-and-drop). The composite key
 * encodes BOTH in a single atomic value, making desync impossible.
 *
 * ## How Ordering Works
 *
 * - Cross-date: The date prefix handles it lexicographically
 *   "2024-01-10T..." < "2024-01-15T..." (Jan 10 before Jan 15)
 *
 * - Same-date: The fractional index is the tiebreaker
 *   "2024-01-15T...--a0" < "2024-01-15T...--a5"
 *
 * - Native string comparison (`<`, `>`) produces correct order
 *
 * ## Key Functions
 *
 * - `buildSortOrder()` — Combine date + frac index into composite
 * - `extractDate()` / `extractFracIndex()` — Parse components
 * - `generateSortOrderRelativeTo()` — Generate sort_order before/after a target item
 * - `generateSortOrderForDate()` — Generate sort_order at chronological position (upload)
 * - `compareSortOrders()` — Compare two items by sort_order
 * - `sortBySortOrder()` — Sort array by sort_order
 * - `normalizeToWallClock()` — Convert any date input to wall-clock string
 *
 * ============================================================================
 */

const SEPARATOR = '--';

// ============================================================================
// Building & Parsing
// ============================================================================

/**
 * Format a Date object as a wall-clock datetime string using local getters.
 * Output: "YYYY-MM-DDTHH:mm:ss.sss" (23 chars, no Z, no offset)
 */
export function formatDateAsWallClock(d: Date): string {
  const Y = d.getFullYear();
  const M = String(d.getMonth() + 1).padStart(2, '0');
  const D = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return `${Y}-${M}-${D}T${h}:${m}:${s}.${ms}`;
}

/**
 * Normalize any date input to a wall-clock datetime string (no timezone).
 * Preserves the local date/time components — never converts to UTC.
 *
 * Output format: "YYYY-MM-DDTHH:mm:ss.sss" (23 chars, no Z, no offset)
 *
 * Handles these input types:
 * - String with timezone offset: strips the offset, keeps local components
 * - String with "Z" suffix: parses as Date, formats with local getters
 * - String without timezone: normalizes millisecond precision, passes through
 * - Date object or epoch ms: formats with local getters
 *
 * @example
 * normalizeToWallClock("2024-04-30T08:30:00+10:00") → "2024-04-30T08:30:00.000"
 * normalizeToWallClock("2024-04-30T08:30:00.000Z")  → local time representation
 * normalizeToWallClock("2024-04-30T08:30:00.000")    → "2024-04-30T08:30:00.000"
 * normalizeToWallClock(new Date())                   → local time representation
 */
export function normalizeToWallClock(date: string | Date | number): string {
  if (typeof date === 'string') {
    // Match timezone offset at end: +HH:MM, -HH:MM, +HHMM, -HHMM, +HH, -HH
    const tzOffsetRegex = /([+-]\d{2}:?\d{2}|[+-]\d{2})$/;
    const hasOffset = tzOffsetRegex.test(date);
    const hasZ = date.endsWith('Z');

    if (hasOffset && !hasZ) {
      // String with timezone offset like "2024-04-30T08:30:00+10:00"
      // Strip the offset to keep the local date/time components
      const stripped = date.replace(tzOffsetRegex, '');
      // Normalize to exactly "YYYY-MM-DDTHH:mm:ss.sss" (23 chars)
      return normalizeWallClockString(stripped);
    }

    if (hasZ) {
      // String ending in Z — this is UTC. Parse and format with local getters.
      // Note: for legacy data this is unavoidable; new data won't have Z.
      const d = new Date(date);
      return formatDateAsWallClock(d);
    }

    // No timezone info — already a wall-clock string. Normalize precision.
    return normalizeWallClockString(date);
  }

  // Date object or epoch number — format with local getters
  const d = typeof date === 'number' ? new Date(date) : date;
  return formatDateAsWallClock(d);
}

/**
 * Normalize a bare datetime string (no timezone) to exactly 23 chars
 * with millisecond precision: "YYYY-MM-DDTHH:mm:ss.sss"
 */
function normalizeWallClockString(s: string): string {
  // Handle "YYYY-MM-DDTHH:mm:ss" (19 chars, no millis)
  if (s.length === 19) return s + '.000';
  // Handle "YYYY-MM-DDTHH:mm:ss.s" or "YYYY-MM-DDTHH:mm:ss.ss"
  const dotIdx = s.indexOf('.', 19);
  if (dotIdx === -1) return s + '.000';
  const millis = s.substring(dotIdx + 1);
  return s.substring(0, dotIdx + 1) + millis.padEnd(3, '0').substring(0, 3);
}

/** @deprecated Use normalizeToWallClock() instead. Kept for backwards compatibility. */
export const normalizeToUTC = normalizeToWallClock;

/**
 * Build composite sort_order from date and fractional index.
 *
 * @example
 * buildSortOrder("2024-01-15T12:00:00.000", "a5") → "2024-01-15T12:00:00.000--a5"
 */
export function buildSortOrder(date: string | Date | number, fracIndex: string): string {
  return `${normalizeToWallClock(date)}${SEPARATOR}${fracIndex}`;
}

/**
 * Extract the wall-clock date portion from a composite sort_order.
 *
 * @example
 * extractDate("2024-01-15T12:00:00.000--a5") → "2024-01-15T12:00:00.000"
 */
export function extractDate(sortOrder: string): string {
  const sepIdx = sortOrder.indexOf(SEPARATOR);
  if (sepIdx === -1) {
    throw new Error(`Invalid sort_order format (missing "${SEPARATOR}" separator): "${sortOrder}"`);
  }
  return sortOrder.substring(0, sepIdx);
}

/**
 * Extract the fractional index portion from a composite sort_order.
 *
 * @example
 * extractFracIndex("2024-01-15T12:00:00.000--a5") → "a5"
 */
export function extractFracIndex(sortOrder: string): string {
  const sepIdx = sortOrder.indexOf(SEPARATOR);
  if (sepIdx === -1) {
    throw new Error(`Invalid sort_order format (missing "${SEPARATOR}" separator): "${sortOrder}"`);
  }
  return sortOrder.substring(sepIdx + SEPARATOR.length);
}

// ============================================================================
// Sorting
// ============================================================================

/**
 * Compare two sort_order strings using native string comparison.
 * Tie-breaking by id for deterministic order.
 *
 * IMPORTANT: Uses native comparison (`<`, `>`), NOT localeCompare().
 * This matches PostgreSQL COLLATE "C" and the fractional-indexing library requirements.
 */
export function compareSortOrders(
  a: { sortOrder: string; id: string },
  b: { sortOrder: string; id: string }
): number {
  if (a.sortOrder < b.sortOrder) return -1;
  if (a.sortOrder > b.sortOrder) return 1;
  // Tie-breaker by ID (shouldn't happen with unique constraint)
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

/**
 * Sort items by sort_order using native string comparison.
 * Returns a new array (does not mutate the original).
 */
export function sortBySortOrder<T extends { sortOrder: string; id: string }>(items: T[]): T[] {
  return [...items].sort(compareSortOrders);
}

// ============================================================================
// Generation: Insert Before/After
// ============================================================================

/**
 * Generate a sort_order relative to a target item ('before' or 'after').
 *
 * The new item ALWAYS gets the target's date. The fractional index is placed
 * before or after the target's frac index (using the neighbor on the other side
 * as the bound, if it shares the same date).
 *
 * This is the ONE function for all positioning operations:
 * - Drag-and-drop: "move before item X" or "move after item X"
 * - Text/music insertion: "insert before selected item" or "insert after last item"
 *
 * @param direction - 'before' or 'after' the target
 * @param targetItemId - ID of the reference item
 * @param sortedItems - All items sorted by sortOrder (ascending)
 * @returns Composite sort_order string
 *
 * @example
 * // Insert before a Jan 1 item (Dec 31 item is predecessor)
 * // → Gets Jan 1 date, frac before the Jan 1 item's frac
 *
 * // Insert after the last item
 * // → Gets last item's date, frac after it
 */
export function generateSortOrderRelativeTo(
  direction: 'before' | 'after',
  targetItemId: string,
  sortedItems: { id: string; sortOrder: string }[]
): string {
  const targetIndex = sortedItems.findIndex(v => v.id === targetItemId);
  if (targetIndex === -1) {
    throw new Error(`Target item ${targetItemId} not found in sortedItems`);
  }

  const target = sortedItems[targetIndex];
  const targetDate = extractDate(target.sortOrder);
  const targetFrac = extractFracIndex(target.sortOrder);

  // The neighbor is the item on the OTHER side from our direction
  const neighborItem = direction === 'before'
    ? (targetIndex > 0 ? sortedItems[targetIndex - 1] : null)
    : (targetIndex < sortedItems.length - 1 ? sortedItems[targetIndex + 1] : null);

  // The neighbor's frac index is only relevant if it shares the target's date.
  // If dates differ, the date prefix alone provides ordering, so we treat the
  // neighbor side as unbounded (null).
  const neighborFrac = neighborItem !== null && extractDate(neighborItem.sortOrder) === targetDate
    ? extractFracIndex(neighborItem.sortOrder)
    : null;

  const newFrac = direction === 'before'
    ? generateKeyBetween(neighborFrac, targetFrac)  // between neighbor and target
    : generateKeyBetween(targetFrac, neighborFrac); // between target and neighbor

  return buildSortOrder(targetDate, newFrac);
}

// ============================================================================
// Generation: Upload (Chronological Insertion)
// ============================================================================

/**
 * Generate a sort_order for a new item at a specific date.
 * Finds the correct insertion point among existing items by comparing dates.
 *
 * Used by: upload flow (assignSortOrders), text/music insertion
 *
 * @param dateTaken - The date for the new item
 * @param existingItems - All existing items sorted by sort_order (ascending)
 * @returns Composite sort_order string
 *
 * @example
 * const existing = [
 *   { sortOrder: "2024-01-10T...--a0" },
 *   { sortOrder: "2024-01-20T...--a0" },
 * ];
 * generateSortOrderForDate("2024-01-15T12:00:00.000", existing)
 * → "2024-01-15T12:00:00.000--a0" (between the two, date prefix handles ordering)
 */
export function generateSortOrderForDate(
  dateTaken: string | Date,
  existingItems: { sortOrder: string }[]
): string {
  const wallClockDate = normalizeToWallClock(dateTaken);

  // Find insertion point: first item whose sort_order is after our date
  // Since sort_order starts with the date prefix, this comparison works correctly
  const insertIndex = existingItems.findIndex(item =>
    extractDate(item.sortOrder) > wallClockDate
  );

  // Get before and after sort_orders at the insertion point
  const before = insertIndex > 0
    ? existingItems[insertIndex - 1].sortOrder
    : (insertIndex === -1 && existingItems.length > 0
      ? existingItems[existingItems.length - 1].sortOrder
      : null);

  const after = insertIndex >= 0 && insertIndex < existingItems.length
    ? existingItems[insertIndex].sortOrder
    : null;

  // When the before neighbor has the same date as us, generate frac index between
  // its frac and the after's frac. When dates differ, just use a fresh frac index.
  const beforeDate = before ? extractDate(before) : null;
  const afterDate = after ? extractDate(after) : null;

  let newFrac: string;

  if (beforeDate === wallClockDate && afterDate === wallClockDate) {
    // Both neighbors share our date — frac index between them
    newFrac = generateKeyBetween(extractFracIndex(before!), extractFracIndex(after!));
  } else if (beforeDate === wallClockDate) {
    // Only before shares our date — frac index after it
    newFrac = generateKeyBetween(extractFracIndex(before!), null);
  } else if (afterDate === wallClockDate) {
    // Only after shares our date — frac index before it
    newFrac = generateKeyBetween(null, extractFracIndex(after!));
  } else {
    // No neighbor shares our date — any frac index works (date prefix handles ordering)
    newFrac = generateKeyBetween(null, null);
  }

  return buildSortOrder(wallClockDate, newFrac);
}

// ============================================================================
// Migration Helpers
// ============================================================================

/**
 * Convert legacy position + date_taken into composite sort_order.
 * Used during database migration backfill.
 */
export function legacyToSortOrder(dateTaken: string, position: string): string {
  return buildSortOrder(dateTaken, position);
}
